import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { formatCurrency, formatDate } from '../../utils/liquidacionHelpers';

/**
 * Componente de diálogo para ver y gestionar detalle de pago
 * @param {boolean} open - Si el diálogo está abierto
 * @param {Function} onClose - Función para cerrar el diálogo
 * @param {Object} pago - Datos del pago completo (pago, servicios, egresos)
 * @param {Object} pagoForm - Formulario de actualización de pago
 * @param {Function} setPagoForm - Setter del formulario
 * @param {Function} onActualizar - Callback al actualizar estado
 * @param {boolean} loading - Estado de carga
 */
const DialogPagoDetalle = ({
  open,
  onClose,
  pago,
  pagoForm,
  setPagoForm,
  onActualizar,
  loading
}) => {
  if (!pago) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          💳 Gestionar Pago #{pago.pago?.id_pago}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Información del trabajador */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: 'rgba(26, 35, 126, 0.05)' }}>
              <Typography variant="h6" gutterBottom>
                👤 {pago.pago.nombre_trabajador} {pago.pago.apellido_trabajador}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                📱 {pago.pago.telefono_trabajador} | 📧 {pago.pago.email_trabajador}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Generado el: {formatDate(pago.pago.fecha_generacion)} por{' '}
                {pago.pago.generado_por_nombre}
              </Typography>
            </Paper>
          </Grid>

          {/* Resumen financiero */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(76, 175, 80, 0.1)' }}>
                  <Typography variant="body2" color="text.secondary">
                    Monto Bruto
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                    {formatCurrency(pago.pago.monto_bruto)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(255, 87, 34, 0.1)' }}>
                  <Typography variant="body2" color="text.secondary">
                    Descuentos
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF5722' }}>
                    -{formatCurrency(pago.pago.monto_descuentos)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(26, 35, 126, 0.1)' }}>
                  <Typography variant="body2" color="text.secondary">
                    Monto Neto
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
                    {formatCurrency(pago.pago.monto_neto)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* Servicios incluidos */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  ✅ Servicios Incluidos ({pago.servicios.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Orden</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Servicio</TableCell>
                        <TableCell align="right">Comisión</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pago.servicios.map((servicio, index) => (
                        <TableRow key={index}>
                          <TableCell>#{servicio.id_orden_servicio}</TableCell>
                          <TableCell>
                            {servicio.nombre_cliente} {servicio.apellido_cliente}
                          </TableCell>
                          <TableCell>{servicio.nombre_servicio}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(servicio.comision_pagada)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Egresos descontados */}
          {pago.egresos.length > 0 && (
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    💸 Egresos Descontados ({pago.egresos.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {pago.egresos.map((egreso, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${egreso.tipo_egreso}: ${egreso.concepto}`}
                          secondary={`${formatDate(egreso.fecha_egreso)} - ${formatCurrency(
                            egreso.monto_descontado
                          )}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {/* Formulario para actualizar estado */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              🔄 Actualizar Estado del Pago
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={pagoForm.estado}
                    onChange={(e) => setPagoForm({ ...pagoForm, estado: e.target.value })}
                    label="Estado"
                  >
                    <MenuItem value="EN_PROCESO">En Proceso</MenuItem>
                    <MenuItem value="PAGADO">Pagado</MenuItem>
                    <MenuItem value="CANCELADO">Cancelado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Método de Pago</InputLabel>
                  <Select
                    value={pagoForm.metodo_pago}
                    onChange={(e) =>
                      setPagoForm({ ...pagoForm, metodo_pago: e.target.value })
                    }
                    label="Método de Pago"
                    disabled={!pagoForm.estado || pagoForm.estado === 'CANCELADO'}
                  >
                    <MenuItem value="TRANSFERENCIA">Transferencia Bancaria</MenuItem>
                    <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                    <MenuItem value="CHEQUE">Cheque</MenuItem>
                    <MenuItem value="OTRO">Otro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Número de Operación"
                  value={pagoForm.numero_operacion}
                  onChange={(e) =>
                    setPagoForm({ ...pagoForm, numero_operacion: e.target.value })
                  }
                  placeholder="Ej: OP123456789"
                  disabled={!pagoForm.estado || pagoForm.estado === 'CANCELADO'}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Observaciones"
                  value={pagoForm.observaciones}
                  onChange={(e) =>
                    setPagoForm({ ...pagoForm, observaciones: e.target.value })
                  }
                  placeholder="Comentarios adicionales..."
                />
              </Grid>
            </Grid>

            {/* Validación visual */}
            {pagoForm.estado === 'PAGADO' && !pagoForm.metodo_pago && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Método de pago es requerido para marcar como PAGADO
              </Alert>
            )}

            {pagoForm.metodo_pago === 'TRANSFERENCIA' && !pagoForm.numero_operacion && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Número de operación es requerido para transferencias
              </Alert>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      {/* DialogActions */}
      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderColor: '#757575',
            color: '#757575',
            '&:hover': {
              borderColor: '#424242',
              backgroundColor: 'rgba(117, 117, 117, 0.04)'
            }
          }}
        >
          Cerrar
        </Button>

        <Button
          variant="contained"
          onClick={onActualizar}
          disabled={loading || !pagoForm.estado}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          sx={{
            background:
              pagoForm.estado === 'PAGADO'
                ? 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)'
                : pagoForm.estado === 'CANCELADO'
                ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)'
                : 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
            '&:disabled': {
              background: '#E0E0E0',
              color: '#999'
            }
          }}
        >
          {loading
            ? 'Actualizando...'
            : pagoForm.estado
            ? `Marcar como ${pagoForm.estado}`
            : 'Actualizar Pago'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogPagoDetalle;
